import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobPreview = ({ formData, onClose }) => {
  const [viewMode, setViewMode] = useState('desktop');

  const companyInfo = {
    name: 'TechCorp Solutions Pvt Ltd',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11efd3bf0-1764221708634.png",
    logoAlt: 'Modern tech company logo with blue and white geometric design representing innovation and technology',
    industry: 'Information Technology',
    size: '500-1000 employees',
    location: 'Bangalore, Karnataka'
  };

  const formatSalary = (range) => {
    const salaryMap = {
      '0-300000': '₹0 - ₹3,00,000',
      '300000-600000': '₹3,00,000 - ₹6,00,000',
      '600000-1000000': '₹6,00,000 - ₹10,00,000',
      '1000000-1500000': '₹10,00,000 - ₹15,00,000',
      '1500000-2500000': '₹15,00,000 - ₹25,00,000',
      '2500000+': '₹25,00,000+'
    };
    return salaryMap?.[range] || 'Not specified';
  };

  const formatExperience = (level) => {
    const expMap = {
      'entry': '0-2 years',
      'mid': '2-5 years',
      'senior': '5-10 years',
      'lead': '10+ years',
      'executive': 'Executive level'
    };
    return expMap?.[level] || level;
  };

  const formatWorkMode = (mode) => {
    const modeMap = {
      'onsite': 'On-site',
      'remote': 'Remote',
      'hybrid': 'Hybrid'
    };
    return modeMap?.[mode] || mode;
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Eye" size={24} color="var(--color-primary)" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Job Preview</h2>
              <p className="text-sm text-muted-foreground">How candidates will see your job posting</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setViewMode('desktop')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-smooth ${
                viewMode === 'desktop' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`
                }>

                <Icon name="Monitor" size={16} className="inline mr-1" />
                Desktop
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-smooth ${
                viewMode === 'mobile' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`
                }>

                <Icon name="Smartphone" size={16} className="inline mr-1" />
                Mobile
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth">

              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
          <div className={`mx-auto bg-background rounded-lg border border-border ${
          viewMode === 'mobile' ? 'max-w-md' : 'max-w-4xl'}`
          }>
            <div className="p-6 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <Image
                    src={companyInfo?.logo}
                    alt={companyInfo?.logoAlt}
                    className="w-full h-full object-cover" />

                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h1 className="text-2xl font-bold text-foreground">{formData?.jobTitle || 'Job Title'}</h1>
                    {formData?.urgentHiring &&
                    <span className="px-3 py-1 bg-error/10 text-error text-xs font-medium rounded-full whitespace-nowrap">
                        Urgent Hiring
                      </span>
                    }
                  </div>
                  <p className="text-lg text-muted-foreground mb-3">{companyInfo?.name}</p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Icon name="MapPin" size={16} />
                      <span>{formData?.locations?.[0]?.city || 'Location'}, {formData?.locations?.[0]?.state || 'State'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Icon name="Briefcase" size={16} />
                      <span>{formatExperience(formData?.experienceLevel)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Icon name="IndianRupee" size={16} />
                      <span>{formatSalary(formData?.salaryRange)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-lg">
                  {formData?.employmentType || 'Full Time'}
                </span>
                <span className="px-3 py-1.5 bg-accent/10 text-accent text-sm font-medium rounded-lg">
                  {formatWorkMode(formData?.workMode)}
                </span>
                {formData?.postingType === 'featured' &&
                <span className="px-3 py-1.5 bg-warning/10 text-warning text-sm font-medium rounded-lg flex items-center gap-1">
                    <Icon name="Star" size={14} />
                    Featured
                  </span>
                }
                {formData?.postingType === 'premium' &&
                <span className="px-3 py-1.5 bg-success/10 text-success text-sm font-medium rounded-lg flex items-center gap-1">
                    <Icon name="Crown" size={14} />
                    Premium
                  </span>
                }
              </div>

              <div className="flex gap-3">
                <Button variant="default" className="flex-1">
                  Apply Now
                </Button>
                <Button variant="outline" iconName="Bookmark">
                  Save
                </Button>
                <Button variant="outline" iconName="Share2">
                  Share
                </Button>
              </div>

              <div className="border-t border-border pt-6 space-y-6">
                {formData?.responsibilities &&
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
                      Key Responsibilities
                    </h3>
                    <div className="text-sm text-muted-foreground whitespace-pre-line">
                      {formData?.responsibilities}
                    </div>
                  </div>
                }

                {formData?.requirements &&
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                      Required Qualifications
                    </h3>
                    <div className="text-sm text-muted-foreground whitespace-pre-line">
                      {formData?.requirements}
                    </div>
                  </div>
                }

                {formData?.qualifications &&
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Award" size={20} color="var(--color-primary)" />
                      Preferred Qualifications
                    </h3>
                    <div className="text-sm text-muted-foreground whitespace-pre-line">
                      {formData?.qualifications}
                    </div>
                  </div>
                }

                {formData?.skills && formData?.skills?.length > 0 &&
                <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Code2" size={20} color="var(--color-primary)" />
                      Required Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {formData?.skills?.map((skill, index) =>
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-muted text-foreground text-sm rounded-lg">

                          {skill?.name}
                        </span>
                    )}
                    </div>
                  </div>
                }

                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">About {companyInfo?.name}</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="Building2" size={16} />
                      <span>{companyInfo?.industry}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Users" size={16} />
                      <span>{companyInfo?.size}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} />
                      <span>{companyInfo?.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              This is how your job posting will appear to candidates
            </p>
            <Button variant="default" onClick={onClose}>
              Close Preview
            </Button>
          </div>
        </div>
      </div>
    </div>);

};

export default JobPreview;